import {
  cloneElement,
  createContext,
  isValidElement,
  useContext,
  useId,
  type HTMLAttributes,
  type ReactElement,
  type ReactNode,
} from 'react';

import { cn } from '@/shared/utils/cn';

import { Label } from './Label';

interface FormFieldContext {
  id: string;
  errorId: string;
  descriptionId: string;
  hasError: boolean;
}

const FormFieldCtx = createContext<FormFieldContext | null>(null);

function useFormField(): FormFieldContext {
  const ctx = useContext(FormFieldCtx);
  if (!ctx) {
    throw new Error('FormField subcomponents must be nested inside <FormField>.');
  }
  return ctx;
}

export interface FormFieldProps extends HTMLAttributes<HTMLDivElement> {
  /** Renders the `*` marker on the label and applies to nested inputs. */
  required?: boolean;
  /** Server / client validation error. Truthy → error styling + aria-describedby. */
  error?: string;
  children: ReactNode;
}

/**
 * Owner of the id / aria plumbing for a single form field.
 */
export function FormField({ error, className, children, ...rest }: FormFieldProps) {
  const id = useId();
  const ctx: FormFieldContext = {
    id,
    errorId: `${id}-error`,
    descriptionId: `${id}-description`,
    hasError: Boolean(error),
  };

  return (
    <FormFieldCtx.Provider value={ctx}>
      <div className={cn('flex flex-col gap-1.5', className)} {...rest}>
        {children}
        {error ? (
          <p id={ctx.errorId} role="alert" className="text-xs text-danger-strong">
            {error}
          </p>
        ) : null}
      </div>
    </FormFieldCtx.Provider>
  );
}

FormField.Label = function FormFieldLabel({
  required,
  children,
}: {
  required?: boolean;
  children: ReactNode;
}) {
  const { id } = useFormField();
  return (
    <Label htmlFor={id} required={required}>
      {children}
    </Label>
  );
};

FormField.Description = function FormFieldDescription({ children }: { children: ReactNode }) {
  const { descriptionId } = useFormField();
  return (
    <p id={descriptionId} className="text-xs text-fg-muted">
      {children}
    </p>
  );
};

interface ControlInjectedProps {
  id?: string;
  'aria-invalid'?: boolean;
  'aria-describedby'?: string;
}

FormField.Control = function FormFieldControl({
  children,
}: {
  children: ReactElement<ControlInjectedProps>;
}) {
  const { id, errorId, descriptionId, hasError } = useFormField();
  const described =
    [hasError ? errorId : null, descriptionId].filter(Boolean).join(' ') || undefined;

  if (!isValidElement<ControlInjectedProps>(children)) return children;

  return cloneElement(children, {
    id,
    'aria-invalid': hasError || children.props['aria-invalid'],
    'aria-describedby': described,
  });
};
